import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Leaf } from "lucide-react";
import { NAV_LINKS } from "@/data/content";
import { AnimatePresence, motion } from "framer-motion";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-background/70 border-b border-border py-3"
          : "bg-transparent border-b border-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 flex items-center justify-between">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-2 group"
        >
          <span className="grid place-items-center w-9 h-9 overflow-hidden">
            <img
              src="https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEilBpMp_L3hl1Sgf1n2B6h8tgCol5s9clcPSv0b6qV6A9TxPONnb7wrQhQ58_3u4v1bPFXDKMMhEwxMR8uhaX3mrpp1rBpp6mR8RKsSMV9JmUbDN00E7y46oiQYNooQfygIBEkA5leFZ5CXKxHeW5Sv-80muZfIO66rIMgs8IgjHCl3eq3hSNjAamJGfOc/s1600/Blue%20Minimalist%20Circle%20Framed%20Instagram%20Profile%20Picture.png"
              alt="FarmingWave logo"
              className="w-full h-full object-cover"
            />
          </span>
          <span className="font-clash font-semibold text-lg tracking-tight text-foreground">
            Farming<span className="text-primary">Wave</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`overline px-4 py-2 transition-colors duration-300 ${
                  active ? "text-primary" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid="navbar-cta"
            className="overline inline-flex items-center bg-primary text-primary-foreground px-5 py-3 font-bold transition-transform duration-300 hover:scale-105"
          >
            Free Consultation
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-foreground"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-background border-t border-border"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-clash text-2xl py-2 text-foreground hover:text-primary transition-colors"
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to="/contact"
                className="overline mt-4 inline-flex justify-center bg-primary text-primary-foreground px-5 py-3 font-bold"
              >
                Free Consultation
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
